import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { Check, UserPlus, X } from 'react-feather';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $nodesOfType,
  type LexicalNode,
  type TextNode,
} from 'lexical';
import { $createMentionNode, MentionNode } from './MentionNode';

export interface MentionCandidate {
  id: string;
  name: string;
  email: string;
}

const initials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? '')
    .join('');

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Character offsets of each "@Name" run (for a currently tagged person) within `text`.
const findMentionRanges = (text: string, names: string[]): { start: number; end: number }[] => {
  if (!text || names.length === 0) return [];
  const pattern = new RegExp(
    `@(?:${[...names]
      .sort((a, b) => b.length - a.length)
      .map(escapeRegExp)
      .join('|')})`,
    'g'
  );
  const ranges: { start: number; end: number }[] = [];
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text))) {
    ranges.push({ start: match.index, end: match.index + match[0].length });
  }
  return ranges;
};

// Turns a plain string back into Lexical nodes for the editor to render, recognizing any
// "@Name" run for a currently tagged person as an atomic MentionNode (see MentionNode.ts)
// rather than plain text, and turning "\n" into real LineBreakNodes.
const buildNodesFromText = (text: string, names: string[]): LexicalNode[] => {
  const ranges = findMentionRanges(text, names);
  const nodes: LexicalNode[] = [];
  const pushPlain = (segment: string) => {
    const lines = segment.split('\n');
    lines.forEach((line, i) => {
      if (line) nodes.push($createTextNode(line));
      if (i < lines.length - 1) nodes.push($createLineBreakNode());
    });
  };
  let cursor = 0;
  for (const { start, end } of ranges) {
    if (start > cursor) pushPlain(text.slice(cursor, start));
    nodes.push($createMentionNode(text.slice(start, end)));
    cursor = end;
  }
  if (cursor < text.length) pushPlain(text.slice(cursor));
  return nodes;
};

const MAX_SUGGESTIONS = 6;
// Matches the dropdown's max-h-48 (192px) Tailwind class.
const MENU_MAX_HEIGHT = 192;

class MentionTypeaheadOption extends MenuOption {
  candidate: MentionCandidate;

  constructor(candidate: MentionCandidate) {
    super(candidate.id);
    this.candidate = candidate;
  }
}

// The menu we actually render into the plugin's anchor. Kept as its own component (rather
// than inline in menuRenderFn) so it can measure and flip itself above the caret when there
// isn't room below — the plugin's own built-in flip check compares against its contentEditable
// root's own top, which for a two-line note box is never far enough away to satisfy it, so it
// never actually flips for a composer this small.
const MentionMenu = ({
  anchorElementRef,
  options,
  selectedIndex,
  selectOptionAndCleanUp,
  setHighlightedIndex,
  flipUp,
}: {
  anchorElementRef: React.RefObject<HTMLElement | null>;
  options: MentionTypeaheadOption[];
  selectedIndex: number | null;
  selectOptionAndCleanUp: (option: MentionTypeaheadOption) => void;
  setHighlightedIndex: (index: number) => void;
  flipUp: boolean;
}) => {
  if (!anchorElementRef.current) return null;
  // The anchor is placed just below the caret line; flipping means sitting the menu's
  // bottom edge just above it instead — shift up by the menu's own height plus roughly
  // one line, mirroring the gap the library already leaves when placing it below.
  const anchorHeight = anchorElementRef.current.offsetHeight || 20;

  return createPortal(
    <div
      className="max-h-48 w-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
      style={{
        // The plugin's own anchor element (what we're portaling into) is a plain
        // position:absolute div with no z-index, so it renders below anything with a
        // real stacking context — e.g. a modal. Give the menu itself a z-index so it
        // isn't stuck behind the surface it's floating over.
        position: 'relative',
        zIndex: 9999,
        transform: flipUp ? `translateY(calc(-100% - ${anchorHeight + 6}px))` : undefined,
      }}>
      {options.map((option, i) => (
        <div
          key={option.key}
          className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer ${
            selectedIndex === i ? 'bg-blue-50' : 'hover:bg-gray-50'
          }`}
          onMouseEnter={() => setHighlightedIndex(i)}
          onClick={() => selectOptionAndCleanUp(option)}>
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-[9px] font-semibold text-white">
            {initials(option.candidate.name)}
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="truncate">{option.candidate.name}</span>
            <span className="truncate text-[10px] text-gray-400">{option.candidate.email}</span>
          </div>
        </div>
      ))}
    </div>,
    anchorElementRef.current
  );
};

// Watches for "@" and shows the suggestion dropdown (positioning/keyboard nav are handled
// by LexicalTypeaheadMenuPlugin itself); picking someone inserts an atomic MentionNode —
// see MentionNode.ts — followed by a plain space, and tags them.
const MentionsPlugin = ({
  candidates,
  taggedUsers,
  onTaggedUsersChange,
  insertedIdsRef,
}: {
  candidates: MentionCandidate[];
  taggedUsers: MentionCandidate[];
  onTaggedUsersChange: (users: MentionCandidate[]) => void;
  insertedIdsRef: React.MutableRefObject<Set<string>>;
}) => {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const checkForMentionMatch = useBasicTypeaheadTriggerMatch('@', { minLength: 0 });

  const options = useMemo(() => {
    if (queryString === null) return [];
    const q = queryString.trim().toLowerCase();
    return candidates
      .filter((c) => !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
      .slice(0, MAX_SUGGESTIONS)
      .map((c) => new MentionTypeaheadOption(c));
  }, [candidates, queryString]);

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(`@${selectedOption.candidate.name}`);
        if (nodeToReplace) nodeToReplace.replace(mentionNode);
        const spaceNode = $createTextNode(' ');
        mentionNode.insertAfter(spaceNode);
        spaceNode.select();
        closeMenu();
      });
      insertedIdsRef.current.add(selectedOption.candidate.id);
      if (!taggedUsers.some((u) => u.id === selectedOption.candidate.id)) {
        onTaggedUsersChange([...taggedUsers, selectedOption.candidate]);
      }
    },
    [editor, insertedIdsRef, taggedUsers, onTaggedUsersChange]
  );

  // Whether the menu (capped at max-h-48, i.e. 192px) would run past the bottom of the
  // viewport if placed below the caret as usual — checked against the contentEditable's
  // own position, which (unlike the plugin's anchor) isn't something this same feature
  // repositions, so there's no race with when it gets measured.
  const rootRect = editor.getRootElement()?.getBoundingClientRect();
  const flipUp = !!rootRect && rootRect.bottom + MENU_MAX_HEIGHT + 8 > window.innerHeight;

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) =>
        options.length > 0 ? (
          <MentionMenu
            anchorElementRef={anchorElementRef}
            options={options}
            selectedIndex={selectedIndex}
            selectOptionAndCleanUp={selectOptionAndCleanUp}
            setHighlightedIndex={setHighlightedIndex}
            flipUp={flipUp}
          />
        ) : null
      }
    />
  );
};

// Keeps the editor's content and the plain-string `value` prop in sync in both
// directions, and auto-untags anyone whose "@Name" mention got deleted.
const MentionSyncPlugin = ({
  value,
  onChange,
  taggedUsers,
  onTaggedUsersChange,
  insertedIdsRef,
}: {
  value: string;
  onChange: (value: string) => void;
  taggedUsers: MentionCandidate[];
  onTaggedUsersChange: (users: MentionCandidate[]) => void;
  insertedIdsRef: React.MutableRefObject<Set<string>>;
}) => {
  const [editor] = useLexicalComposerContext();
  const lastEmittedRef = useRef<string | null>(null);

  // Rebuild the editor's content when `value` changes from OUTSIDE this component — e.g.
  // switching which note is being edited, or the composer clearing after submit — never in
  // response to our own onChange below, which would otherwise wipe cursor position and undo
  // history on every keystroke.
  useLayoutEffect(() => {
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    const names = taggedUsers.map((u) => u.name);
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const paragraph = $createParagraphNode();
      const nodes = buildNodesFromText(value, names);
      paragraph.append(...(nodes.length > 0 ? nodes : [$createTextNode('')]));
      root.append(paragraph);
    });
    // Only re-sync when `value` itself changes from the outside — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const text = $getRoot().getTextContent();
          lastEmittedRef.current = text;
          onChange(text);

          // If someone deletes the "@Name" text for a mention we inserted, drop them from
          // taggedUsers too — the mention itself is the only affordance for removing it.
          if (insertedIdsRef.current.size === 0) return;
          const stillPresent = new Set($nodesOfType(MentionNode).map((n) => n.getTextContent()));
          const toDrop = [...insertedIdsRef.current].filter((id) => {
            const user = taggedUsers.find((u) => u.id === id);
            return !user || !stillPresent.has(`@${user.name}`);
          });
          if (toDrop.length > 0) {
            toDrop.forEach((id) => insertedIdsRef.current.delete(id));
            onTaggedUsersChange(taggedUsers.filter((u) => !toDrop.includes(u.id)));
          }
        });
      }),
    [editor, onChange, onTaggedUsersChange, taggedUsers, insertedIdsRef]
  );

  return null;
};

/**
 * A lightweight note composer with "@" mention autocomplete, built on Lexical (already used
 * elsewhere in the app for rich text) so a tagged "@Name" is a real inline node with a native
 * caret — no separate highlight layer to keep in sync, unlike a plain <textarea> would need.
 * Typing "@" opens a live suggestion list (filtered by name/email); picking someone inserts
 * "@Name" right where you were typing, styled as a pill, and it deletes as one atomic unit.
 * The note itself is still a plain string in and out (`value`/`onChange`) — a mention is just
 * "@Name" text under the hood, so nothing downstream (saved notes, notifications) needs to
 * know Lexical is involved. Pair with `TagPeopleButton` (rendered wherever it fits your
 * layout, e.g. next to an "Add" button) to let people tag someone without typing "@" at all;
 * those tags (and any tag carried over from before this text existed, e.g. editing an older
 * note) show as a removable chip below since there's no "@Name" run in the text to anchor to.
 */
const MentionTextarea = ({
  value,
  onChange,
  candidates,
  taggedUsers,
  onTaggedUsersChange,
  placeholder,
  rows = 2,
  className,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  candidates: MentionCandidate[];
  taggedUsers: MentionCandidate[];
  onTaggedUsersChange: (users: MentionCandidate[]) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  autoFocus?: boolean;
}) => {
  // Ids tagged by picking a suggestion *in this component instance* — i.e. ones we know
  // have a literal "@Name" anchor in the text. Only these get auto-untagged when their
  // "@Name" is deleted; tags carried in from outside (e.g. an older note's mentionedUserIds,
  // which never had anchor text) are left alone and fall back to the removable chip below.
  const insertedIdsRef = useRef<Set<string>>(new Set());

  const initialConfig = useMemo(
    () => ({
      namespace: 'MentionNote',
      nodes: [MentionNode],
      theme: {},
      onError: (error: Error) => console.error(error),
    }),
    []
  );

  const placeholderProps = placeholder
    ? {
        'aria-placeholder': placeholder,
        placeholder: (
          <div className="pointer-events-none absolute left-0 top-0 select-none text-gray-400 text-xs">
            {placeholder}
          </div>
        ),
      }
    : { placeholder: null };

  // Tags with no "@Name" anchor in the current text (picked via TagPeopleButton, or
  // carried over from an older note that predates inline mentions) — shown as a
  // removable chip below since there's nowhere inline to render/remove them.
  const chipOnlyUsers = taggedUsers.filter((u) => !value.includes(`@${u.name}`));

  return (
    <div className="w-full">
      <LexicalComposer initialConfig={initialConfig}>
        <div className="relative w-full">
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                className={twMerge(className, 'overflow-y-auto')}
                style={{ minHeight: `${rows * 1.625}em` }}
                {...placeholderProps}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          {autoFocus && <AutoFocusPlugin />}
          <MentionsPlugin
            candidates={candidates}
            taggedUsers={taggedUsers}
            onTaggedUsersChange={onTaggedUsersChange}
            insertedIdsRef={insertedIdsRef}
          />
          <MentionSyncPlugin
            value={value}
            onChange={onChange}
            taggedUsers={taggedUsers}
            onTaggedUsersChange={onTaggedUsersChange}
            insertedIdsRef={insertedIdsRef}
          />
        </div>
      </LexicalComposer>

      {chipOnlyUsers.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {chipOnlyUsers.map((u) => (
            <span
              key={u.id}
              className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
              @{u.name}
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onTaggedUsersChange(taggedUsers.filter((t) => t.id !== u.id))}
                className="cursor-pointer border-none bg-transparent p-0 leading-none text-blue-400 hover:text-blue-700">
                <X size={9} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Standalone "tag someone" button + searchable people picker, meant to sit next to
 * whatever action button submits the note (e.g. "Add"). Independent of any particular
 * textarea's live "@" state — it edits the same `taggedUsers` a MentionTextarea next to
 * it would, so the two stay in sync no matter which one a person uses. Tagging never
 * touches the note text itself — just the tagged-people list.
 */
export const TagPeopleButton = ({
  candidates,
  taggedUsers,
  onTaggedUsersChange,
}: {
  candidates: MentionCandidate[];
  taggedUsers: MentionCandidate[];
  onTaggedUsersChange: (users: MentionCandidate[]) => void;
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0, width: 240 });

  const matches = candidates.filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
  });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Search input (~36px) + up to max-h-48 (192px) of results; flip above the
      // button when there isn't enough room below so it doesn't get clipped.
      const estimatedHeight = 228;
      const fitsBelow = rect.bottom + 4 + estimatedHeight <= window.innerHeight;
      setPosition({
        top: fitsBelow ? rect.bottom + 4 : Math.max(rect.top - estimatedHeight - 4, 8),
        left: Math.max(rect.right - 240, 8),
        width: 240,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setQuery('');
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const toggleCandidate = (candidate: MentionCandidate) => {
    const alreadyTagged = taggedUsers.some((u) => u.id === candidate.id);
    onTaggedUsersChange(
      alreadyTagged ? taggedUsers.filter((u) => u.id !== candidate.id) : [...taggedUsers, candidate]
    );
  };

  if (candidates.length === 0) return null;

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        title="Tag someone"
        onClick={(e) => {
          e.preventDefault();
          setIsOpen((open) => !open);
        }}
        className={`flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-lg border transition-colors ${
          isOpen
            ? 'border-blue-200 bg-blue-50 text-blue-600'
            : 'border-gray-200 bg-white text-gray-400 hover:bg-gray-50'
        }`}>
        <UserPlus size={13} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              zIndex: 9999,
            }}
            className="rounded-lg border border-gray-200 bg-white shadow-lg">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search people..."
              className="w-full rounded-t-lg border-b border-gray-200 px-3 py-2 text-xs outline-none"
            />
            <div className="max-h-48 overflow-y-auto">
              {matches.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-500">No people found</div>
              ) : (
                matches.map((c) => {
                  const isTagged = taggedUsers.some((u) => u.id === c.id);
                  return (
                    <div
                      key={c.id}
                      onClick={() => toggleCandidate(c)}
                      className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer ${
                        isTagged ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}>
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-[9px] font-semibold text-white">
                        {initials(c.name)}
                      </span>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate">{c.name}</span>
                        <span className="truncate text-[10px] text-gray-400">{c.email}</span>
                      </div>
                      {isTagged && <Check size={12} className="shrink-0 text-blue-500" />}
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default MentionTextarea;
